'use client';

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Typography from '@/components/atoms/project-builder/typography';
import Button from '@/components/atoms/project-builder/button';
import Link from '@/components/atoms/link';
import { useRouter, useParams } from 'next/navigation';
import { tablet } from '@/lib/utils/screensizes';
import useMediaQuery from '@/hooks/useMediaQuery';
import Icon from '@/components/icons/x-mark';
import Popup from '@/components/atoms/project-builder/popup';
import Spinner from '@/components/organisms/spinner';
import { projectObj } from '@/lib/tastics/project-builder-page/components/data';
import Input from '@/components/atoms/project-builder/input';
import useAccount from '@/lib/hooks/useAccount';
import useProjectBuilder from '@/lib/hooks/useProjectBuilder';
import { TotalCountState } from '@/lib/tastics/project-builder-page/dashboard';
import useWindowWidth from '@/helpers/hooks/useBrowerWidth';
import Table from '@/components/organisms/project-builder/table';
import Radio from '@/components/atoms/project-builder/radio';
import Modal from '@/components/organisms/modal';
import ProjectBuilderGuide from '../../project-builder-guide';


interface Project {
  projectLocation: string;
  lastModifiedAt: string | undefined;
  createdAt: string | undefined;
  projectType: string | undefined;
  projectName: string | undefined;
  projectId: string;
  name: string;
  projectCreatedDate?: string | undefined;
  status: 'open' | 'closed';
  username: string;
  shoppingListId: string;
  version: number;
}

const optionsData = [
  { name: 'Title A to Z', checked: false, value: 'nameAsc' },
  { name: 'Title Z to A', checked: false, value: 'nameDesc' },
  { name: 'Project Type A to Z', checked: false, value: 'projectAsc' },
  { name: 'Project Type Z to A', checked: false, value: 'projectDesc' },
  { name: 'Location A to Z', checked: false, value: 'locationAsc' },
  { name: 'Location Z to A', checked: false, value: 'locationDesc' },
  { name: 'CreatedAt Asc', checked: false, value: 'createdAtAsc' },
  { name: 'CreatedAt Desc', checked: false, value: 'createdAtDesc' },
  { name: 'ModifiedAt Asc', checked: false, value: 'lastModifiedAtAsc' },
  { name: 'ModifiedAt Desc', checked: false, value: 'lastModifiedAtDesc' },
];

const pageBtnClasses = {
  base: 'border px-2 py-2 text-sm font-medium text-gray-500',
  main: 'cursor-not-allowed bg-gray-300 text-gray-500',
  fallback: 'bg-white text-gray-500 hover:bg-gray-200',
};

enum TAB_STATES {
  my = 'my',
  open = 'open',
  close = 'closed',
  completed = 'completed',
}

const tableHeader = [
  {
    label: 'Title',
    sort: true,
    valueAsc: 'nameAsc',
    valueDesc: 'nameDesc',
  },
  {
    label: 'Project Type',
    sort: true,
    valueAsc: 'projectTypeAsc',
    valueDesc: 'projectTypeDesc',
  },
  {
    label: 'Location',
    sort: true,
    valueAsc: 'locationAsc',
    valueDesc: 'locationDesc',
  },
  {
    label: 'Date Created',
    sort: true,
    valueAsc: 'createdAtAsc',
    valueDesc: 'createdAtDesc',
  },
  {
    label: 'Date Modified',
    sort: true,
    valueAsc: 'lastModifiedAtAsc',
    valueDesc: 'lastModifiedAtDesc',
  },
];

const formatDate = (date: string | undefined) => {
  return date
    ? new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
    : 'N/A';
};

interface ProjectList {
  count: number;
  limit: number;
  offset: number;
  total: number;
  results: Project[];
}

interface DeleteProjectPayload {
  id: string;
  version: number;
}

interface UpdateProjectStatusPayload {
  shoppingListId: string;
  version: number;
  status: string;
  fieldName: string;
}

interface ProjectListProps {
  totalRecords: number;
  totalCount: TotalCountState;
  getMyProjectsDashboard: (payload: { limit: number; sortKey?: string; status: string | string[]; offset: number }) => any;
  deleteProject: (payload: DeleteProjectPayload) => any;
  updateProjectStatus: (payload: UpdateProjectStatusPayload) => any;
  setTotalCount: React.Dispatch<React.SetStateAction<any>>;
  dateRangeDetails: any;
  setDateRangeDetails: React.Dispatch<React.SetStateAction<any>>;
}

const ProjectList: React.FC<ProjectListProps> = ({
  totalRecords,
  totalCount,
  getMyProjectsDashboard,
  deleteProject,
  updateProjectStatus,
  setTotalCount,
  dateRangeDetails,
  setDateRangeDetails,
}) => {
  const { locale } = useParams() as { locale?: string };
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<TAB_STATES>(TAB_STATES.open);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const initialMount = useRef(true);
  const skipCountsRef = useRef(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [projects, setProjects] = useState<ProjectList>(
    {
      count: 0,
      limit: 0,
      offset: 0,
      total: totalRecords,
      results: [],
    }
  );
  const [loading, setLoading] = useState<any>(true);
  const [openSort, setOpenSort] = useState<any>(false);
  const [offset, setOffset] = useState(0);
  const [highlightedSort, setHighlightedSort] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [isTablet] = useMediaQuery(tablet);
  const width = useWindowWidth();

  const [sortState, setSortState] = useState<{
    column: number | null;
    direction: 'up' | 'down' | null;
  }>({
    column: null,
    direction: null,
  });
  const [options, setOptions] = useState(optionsData);
  const [sortingKey, setSortingKey] = useState('');
  const isTogglingFilters = useRef(false);

  const router = useRouter();
  const STATUSES = useMemo(
    () => ({
      my: 'open',
      open: 'open',
      closed: 'closed',
      completed: 'closed',
    }),
    [],
  );
  const prevFiltersRef = useRef({
    activeTab,
    sortingKey,
    currentPage,
    dateRangeDetails,
  });

  const shouldSkipFetch = useMemo(() => {
    return false;
  }, []);

  useEffect(() => {
    setOpenDropdown(null);
  }, [activeTab]);

  const fetchProjects = useCallback(async () => {
    if (shouldSkipFetch) {
      setProjects({
        count: 0,
        limit: 0,
        offset: 0,
        total: totalRecords,
        results: [],
      });
      setLoading(false);
      return;
    }
    try {
      const payload: any = {
        limit: itemsPerPage,
        status: STATUSES[activeTab],
        offset: (currentPage - 1) * itemsPerPage,
      };

      if (sortingKey) payload.sortKey = sortingKey;
      if (!highlightedSort) payload.sortKey = 'lastModifiedAtDesc';

      payload.startDate = dateRangeDetails?.startDate ?? null;
      payload.endDate = dateRangeDetails?.endDate ?? null;
      payload.status = STATUSES[activeTab];
      payload.page = 'dashboard list';

      const responseBody: any = await getMyProjectsDashboard(payload);

      if (['nameDesc', 'nameAsc'].includes(sortingKey) && responseBody?.data?.results?.length) {
        const sortedResults = [...responseBody.data.results].sort((a: any, b: any) =>
          a.projectName.localeCompare(b.projectName),
        );
        setProjects({
          ...responseBody.data,
          results: sortedResults,
        });
      } else {
        setProjects(responseBody.data);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  }, [
    activeTab,
    sortingKey,
    currentPage,
    dateRangeDetails,
    shouldSkipFetch,
    STATUSES,
    highlightedSort,
    itemsPerPage,
    getMyProjectsDashboard,
  ]);

  const fetchData = useCallback(
    (skip: boolean) => {
      fetchProjects();
    },
    [fetchProjects],
  );
  const closeModal = () => {
    setIsModalOpen(false);
  };

  const openProjectGuides = useCallback((e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    requestAnimationFrame(() => setIsModalOpen(true));
  }, []);

  useEffect(() => {
    isModalOpen && document.body.classList.add('root_overflow_hidden');
    !isModalOpen && document.body.classList.remove('root_overflow_hidden');
  }, [isModalOpen]);

  useEffect(() => {
    if (!isModalOpen) return;
    if (!locale) return;

    let cancelled = false;

    const loadProjectGuide = async () => {
      try {
        if (cancelled) return;
      } finally {
      }
    };

    loadProjectGuide();

    return () => {
      cancelled = true;
    };
  }, [isModalOpen, locale]);
  const debouncedFetchProjects = useCallback(
    (skipCounts = false) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      skipCountsRef.current = skipCounts;

      debounceTimerRef.current = setTimeout(() => {
        fetchData(skipCountsRef.current);
      }, 300);
    },
    [fetchData],
  );

  useEffect(() => {
    if (isTogglingFilters.current) {
      isTogglingFilters.current = false;
      return;
    }

    if (initialMount.current) {
      fetchProjects();
      initialMount.current = false;
      return;
    }
    const currentFilters = {
      activeTab,
      sortingKey,
      currentPage,
      dateRangeDetails,
    };

    const prevFilters = prevFiltersRef.current;

    const activeTabChanged = currentFilters.activeTab !== prevFilters.activeTab;
    const currentPageChanged = currentFilters.currentPage !== prevFilters.currentPage;
    const sortingChanged = currentFilters.sortingKey !== prevFilters.sortingKey;

    const filtersChanged =
      activeTabChanged ||
      sortingChanged ||
      currentPageChanged ||
      currentFilters.dateRangeDetails !== prevFilters.dateRangeDetails;

    if (filtersChanged) {
      const skipCounts = activeTabChanged || currentPageChanged || sortingChanged;
      debouncedFetchProjects(skipCounts);
      prevFiltersRef.current = currentFilters;
    }
  }, [
    activeTab,
    sortingKey,
    currentPage,
    dateRangeDetails,
    debouncedFetchProjects,
  ]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleOperationSuccess = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setTimeout(() => {
      fetchProjects();
    }, 500);
  }, [fetchProjects]);

  const handleTabChange = (key: TAB_STATES) => {
    if (activeTab !== key) {
      setLoading(true);
    }
    isTogglingFilters.current = false;
    setActiveTab(key);
    setCurrentPage(1);
  };

  const getTabComponents = useMemo(() => {
    return (
      <div className="tabs-header">
        <button onClick={() => handleTabChange(TAB_STATES.open)} className={`tab-button ${activeTab === TAB_STATES.open && 'active'}`}>
          Open Projects
        </button>
        <button onClick={() => handleTabChange(TAB_STATES.close)} className={`tab-button ${activeTab === TAB_STATES.close && 'active'}`}>
          Closed Projects
        </button>
      </div>
    );
  }, [activeTab, handleTabChange]);

  const getPaginationComponent = useMemo(() => {
    const generatePageNumbers = () => {
      const pages: (number | string)[] = [];
      const isMobile = width && width < 640;
      const range = isMobile ? 1 : 2;

      if (totalPages <= range * 2 + 5) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);

        if (currentPage > range + 2) {
          pages.push('...');
        }

        const start = Math.max(2, currentPage - range);
        const end = Math.min(totalPages - 1, currentPage + range);

        for (let i = start; i <= end; i++) {
          pages.push(i);
        }

        if (currentPage < totalPages - (range + 1)) {
          pages.push('...');
        }

        pages.push(totalPages);
      }

      return pages;
    };

    const totalPages = Math.ceil(projects?.total / itemsPerPage);
    const pageNumbers = generatePageNumbers();

    return (
      <div className="mt-4 flex flex-wrap items-center justify-center">
        {currentPage > 1 && (
          <button onClick={() => handlePageChange(currentPage - 1)} className={`${pageBtnClasses.base} px-1 py-0.5 text-[20px] cursor-pointer`}>
            ‹
          </button>
        )}

        <div className="flex flex-wrap justify-center gap-1">
          {pageNumbers.map((page, index) => (
            <button
              key={index}
              disabled={page === '...'}
              onClick={() => typeof page === 'number' && handlePageChange(page)}
              className={`${pageBtnClasses.base} ${page === currentPage ? 'bg-gray-200 text-black' : page === '...' ? 'cursor-default text-gray-400' : pageBtnClasses.fallback} !px-2 sm:px-3 py-1.5 cursor-pointer`}
            >
              {page}
            </button>
          ))}
        </div>

        {currentPage < totalPages && (
          <button onClick={() => handlePageChange(currentPage + 1)} className={`${pageBtnClasses.base} px-1 py-0.5 text-[20px] cursor-pointer`}>
            ›
          </button>
        )}
      </div>
    );
  }, [currentPage, projects?.total, width]);

  const handleSort = useCallback(
    (index: number) => {
      const updatedOptions = options.map((option, i) => ({
        ...option,
        checked: i === index,
      }));
      setOptions(updatedOptions);
      setSortingKey(updatedOptions[index].value);
      setHighlightedSort(true);
    },
    [options],
  );

  const handleSortForDesktop = useCallback(
    (index: number, direction: 'up' | 'down', sortingValue?: string) => {
      if (sortState.column === index && sortState.direction === direction) {
        setSortState({
          column: null,
          direction: null,
        });
        setHighlightedSort(false);
        setSortingKey('lastModifiedAtDesc');
      } else {
        setSortState({
          column: index,
          direction,
        });
        setHighlightedSort(true);
        if (sortingValue) {
          setSortingKey(sortingValue);
        }
      }
    },
    [sortState],
  );

  const handleClose = useCallback(() => {
    setOpenSort(false);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    const offsetValue = (newPage - 1) * itemsPerPage;
    setCurrentPage(newPage);
    setOffset(offsetValue);
    const element = document.getElementById('list-container');
    if (element) {
      window.scrollTo({
        top: element.offsetTop,
        behavior: 'smooth',
      });
    }
  }, []);

  if (loading) {
    return <>
      <Spinner />
      <div className="overlay fixed left-0 top-0 z-50 h-full w-full bg-gray-800 opacity-75"></div>
    </>;
  }

  return (
    <>
      <ProjectHeadingSection />
      <div className="project-list">
        {getTabComponents}
        {isTablet ? (
          <>
            <TableView
              projects={projects}
              sortState={sortState}
              handleSortForDesktop={handleSortForDesktop}
              ProjectDetail={ProjectDetail}
              setOpenDropdown={setOpenDropdown}
              openDropdown={openDropdown}
              handleOperationSuccess={handleOperationSuccess}
              activeTab={activeTab}
              deleteProject={deleteProject}
              updateProjectStatus={updateProjectStatus}
            />
            {!projects?.results?.length ? (
              <div className='justify-center flex mt-[25px]'><p>No Records Found.</p></div>
            ) : (
              getPaginationComponent
            )}
          </>
        ) : (
          <>
            <MobileView
              openSort={openSort}
              setOpenSort={setOpenSort}
              options={options}
              handleSort={handleSort}
              handleClose={handleClose}
              projects={projects}
              setOpenDropdown={setOpenDropdown}
              openDropdown={openDropdown}
              handleOperationSuccess={handleOperationSuccess}
              activeTab={activeTab}
              deleteProject={deleteProject}
              updateProjectStatus={updateProjectStatus}
            />
            {!projects?.results?.length ? <><p>No Records Found.</p></> : getPaginationComponent}
            
          </>
        )}
      </div>
      <div>
        <Modal
          className="mx-auto flex !w-[95vw] max-w-[1216px] flex-col items-center justify-center sm:-mb-11 sm:overflow-auto md:mb-0 md:mt-0 xl:!w-full"
          isOpen={isModalOpen}
          onRequestClose={closeModal}
          preventScroll={false}
          closeButton={true}
          variant="default"
          centered={true}
          size="fit"
          style={{ overlay: { zIndex: 9999 } }}
        >
          <div className="relative mx-auto !h-[90vh] overflow-y-auto !px-[18px] sm:h-[80vh] sm:max-h-[660px] sm:!px-[40px]">
            <ProjectBuilderGuide autoOpenAccordion={true} />
          </div>

        </Modal>
      </div>
    </>
  );
};

const ProjectDetailComponent = ({ label, value }: { label?: string; value?: string }) => {
  return (
    <div>
      {label && <Typography className="text-12">{label}</Typography>}
      {value && <Typography className="whitespace-normal text-16 !font-medium leading-6 text-[#090909]">{value || 'NA'}</Typography>}
    </div>
  );
};
export const ProjectDetail = React.memo(ProjectDetailComponent);

interface ProjectListRecordsComponentProps {
  projects: any;
  setOpenDropdown: React.Dispatch<React.SetStateAction<any>>;
  openDropdown: any;
  handleOperationSuccess: () => void;
  activeTab: string;
  deleteProject: (payload: DeleteProjectPayload) => void;
  updateProjectStatus: (payload: UpdateProjectStatusPayload) => void;
}

const ProjectListRecordsComponent = ({ projects, setOpenDropdown, openDropdown, handleOperationSuccess, activeTab, deleteProject, updateProjectStatus }: ProjectListRecordsComponentProps) => {
  return (
    <div className="mx-auto max-w-4xl">
      {projects?.results?.map((project: any, index: any) => (
        <ProjectCard
          key={project.projectId}
          project={project}
          isEvenRow={index % 2 !== 0}
          setOpenDropdown={setOpenDropdown}
          openDropdown={openDropdown}
          handleOperationSuccess={handleOperationSuccess}
          activeTab={activeTab}
          deleteProject={deleteProject}
          updateProjectStatus={updateProjectStatus}
        />
      ))}
    </div>
  );
};
export const ProjectListRecords = React.memo(ProjectListRecordsComponent);

interface ProjectCardProps {
  project: Project;
  isEvenRow: boolean;
  setOpenDropdown: React.Dispatch<React.SetStateAction<any>>;
  openDropdown: any;
  handleOperationSuccess: () => void;
  activeTab: string;
  deleteProject: (payload: DeleteProjectPayload) => void;
  updateProjectStatus: (payload: UpdateProjectStatusPayload) => void;
}

const ProjectCardComponent = ({ project, isEvenRow, setOpenDropdown, openDropdown, handleOperationSuccess, deleteProject, updateProjectStatus, activeTab }: ProjectCardProps) => {
  return (
    <div key={project.projectId} className={`p-1 ${isEvenRow ? 'bg-[#DDDDDD]' : ''}`}>
      <div>
        <div className="projectName mb-[18px] flex items-baseline justify-between">
          <div className="break-words">
            <Typography className="text-12">Title</Typography>
            <Link link={`view?projectId=${project?.shoppingListId}`}>
              <Typography className="decoration-3 whitespace-normal text-16 !font-medium leading-6 text-[#090909] underline">{project.projectName}</Typography>
            </Link>
          </div>
          <div>
            <DropDown
              project={project}
              setOpenDropdown={setOpenDropdown}
              openDropdown={openDropdown}
              handleOperationSuccess={handleOperationSuccess}
              activeTab={activeTab}
              deleteProject={deleteProject}
              updateProjectStatus={updateProjectStatus}
            />
          </div>
        </div>
        <ProjectDetail label="Project Type" value={projectObj[project?.projectType ?? ''] || 'NA'} />
        <ProjectDetail label="Location" value={project.projectLocation || 'NA'} />
        <ProjectDetail label="Date Created" value={project?.projectCreatedDate ? formatDate(project?.projectCreatedDate) : 'N/A'} />
        <ProjectDetail label="Date Modified" value={formatDate(project.lastModifiedAt)} />
      </div>
    </div>
  );
};
export const ProjectCard = React.memo(ProjectCardComponent);

interface CloneProjectPopupProps {
  isOpen: boolean;
  onClose: () => void;
  formInput: string;
  formError: string;
  onConfirm: () => void;
  selectedProject: any;
  setFormInput: any;
  clonePrjtSuccess: string;
  clonePrjtError: string;
  cloningInProgress: boolean;
}

const CloneProjectPopupComponent = ({ isOpen, onClose, formInput, formError, onConfirm, selectedProject, setFormInput, clonePrjtSuccess, clonePrjtError, cloningInProgress }: CloneProjectPopupProps) => {
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormInput(event.target.value);
    },
    [setFormInput],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const invalidChars = /[!@#$%^&*()_+={}\[\];:'"\\|,.<>?~`]/;
      if (invalidChars.test(event.key)) {
        event.preventDefault();
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        const inputValue = (event.target as HTMLInputElement).value;
        const sanitizedValue = inputValue.replace(/[!@#$%^&*()_+={}\[\];:'"\\|,.<>?~`]/g, '').substring(0, 50);
        setFormInput(sanitizedValue);
      }
    },
    [setFormInput],
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();
      let pastedData = event.clipboardData.getData('text');
      pastedData = pastedData.replace(/[!@#$%^&*()_+={}\[\];:'"\\|,.<>?~`]/g, '').substring(0, 50);
      setFormInput(pastedData);
    },
    [setFormInput],
  );

  return (
    <Popup isOpen={isOpen} bodyClassNames="mx-5 sm:mx-unset max-w-[535px]">
      <div onClick={(e) => e.stopPropagation()} className="mb-[20px] flex justify-between pl-[25px] pt-[32px]">
        <div>
          <Typography className="flex flex-1 justify-start !text-[20px] !text-[#4B5563]">Create Duplicate Project</Typography>
          <Typography as="span" className="flex flex-1 justify-start !text-[16px] !text-[4B5563]">{`Project: ${selectedProject?.name}`}</Typography>
        </div>
        <div className="cursor-pointer pr-4" onClick={onClose}>
          <Icon />
        </div>
      </div>

      <div className="pl-[25px] pr-[25px] pt-[20px]">
        <Input
          name="specificationName"
          placeholder={'New Project Name'}
          value={(formInput as string) ?? ''}
          error={formError}
          onChange={handleInputChange}
          onPaste={(event) => handlePaste(event)}
          onKeyDown={(event) => handleKeyDown(event)}
          tabIndex={0}
          autoComplete=""
          className={`mb-[20px] h-[40px] text-[18px] !p-[12px] ${formError ? '!border-red-500' : '!border-[#A7A7A7]'} !rounded-[6px] `}

          maxLength={50}
        />
      </div>
      <Typography as="span" className="flex flex-1 justify-start pl-6 pr-6 !text-16 !text-[green]">{clonePrjtSuccess}</Typography>
      <Typography as="span" className="flex flex-1 justify-start pl-6 pr-6 !text-16 !text-[red]">{clonePrjtError}</Typography>
      <div className="m-2.5 flex justify-center ">
        <Button disabled={cloningInProgress} onClick={onConfirm} variant="primary" size="m" className={`!bg-[#1A1A1A] !py-[12px] !px-[36px] cursor-pointer ${cloningInProgress ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
          Create
        </Button>
      </div>
    </Popup>
  );
};
export const CloneProjectPopup = React.memo(CloneProjectPopupComponent);

interface DropDownProps {
  project: any;
  setOpenDropdown: React.Dispatch<React.SetStateAction<any>>;
  openDropdown: any;
  handleOperationSuccess: () => void;
  activeTab: string;
  deleteProject: (payload: DeleteProjectPayload) => void;
  updateProjectStatus: (payload: UpdateProjectStatusPayload) => void;
}

const DropDownCoponent = ({ project, setOpenDropdown, openDropdown, handleOperationSuccess, activeTab, deleteProject, updateProjectStatus }: DropDownProps) => {
  const router = useRouter();
  const { account } = useAccount();
  const { cloneProject } = useProjectBuilder();

  const dropdownRef = useRef<HTMLDivElement>(null);
  const [openPopup, setOpenPopup] = useState<boolean>(false);
  const [selectedProject, setSelectedProject] = useState<{ id: string; projectId: string; version: number; name: string } | null>(null);
  const [dropDownState, setDropDownState] = useState('');
  const [isClonePopupOpen, setIsClonePopupOpen] = useState(false);
  const [formInput, setFormInput] = useState('');
  const [formError, setFormError] = useState('');
  const [clonePrjtSuccess, setClonePrjtSuccess] = useState('');
  const [clonePrjtError, setClonePrjtError] = useState('');
  const [cloningInProgress, setCloningInProgress] = useState(false);
  console.log("----cloningInProgress", cloningInProgress);

  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };
    if (openDropdown === project.projectId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown, project.projectId]);

  const openPopupDialog = useCallback((id: string, projectId: string, version: number, name: string, dropdown: string) => {
    setOpenPopup(true);
    setSelectedProject({ id, projectId, version, name });
    setDropDownState(dropdown);
  }, []);

  const handlePopupClose = useCallback((response: 'success' | 'canceled') => {
    if (response === 'canceled') {
      setOpenPopup(false);
    } else if (response === 'success') {
      setOpenPopup(false);
      setOpenDropdown(null);
    }
  }, []);

  const toggleDropdown = useCallback((projectId: string) => {
    setOpenDropdown((prev: any) => (prev === projectId ? null : projectId));
  }, []);

  const navigateToForm = useCallback(
    (path: string) => {
      setOpenDropdown(null);
      router.push(`${path}/?projectId=${project?.shoppingListId}`);
    },
    [project?.shoppingListId, router],
  );

  const handleOpenClone = useCallback((project: any) => {
    setClonePrjtSuccess('');
    setClonePrjtError('');
    setFormError('');
    setFormInput('');
    setIsClonePopupOpen(true);
    setSelectedProject({
      id: project.shoppingListId,
      projectId: project.projectId,
      version: project.version,
      name: project.projectName,
    });
  }, []);

  const handleCloseClone = useCallback(() => {
    setClonePrjtSuccess('');
    setClonePrjtError('');
    setFormError('');
    setFormInput('');
    setIsClonePopupOpen(false);
  }, []);

  const handleConfirmClone = useCallback(async () => {
    setClonePrjtSuccess('');
    setClonePrjtError('');
    setFormError('');
    if (!formInput.trim()) {
      setFormError('Project name is required.');
      return;
    }
    try {
      setCloningInProgress(true);
      const payload = {
        projectName: formInput.trim(),
        customerId: account?.accountId,
        shoppingListId: selectedProject?.id,
      };
      const response: any = await cloneProject(payload);
      if (response.data.code == 'SUCCESS') {
        setClonePrjtSuccess('Project cloned successfully.');
        handleOperationSuccess();
        setTimeout(() => {
          setIsClonePopupOpen(false);
          setOpenDropdown(null);
          setCloningInProgress(false);
        }, 3000);
      }
    } catch (error: any) {
      const errorMessage = error.message?.replace(/^Error:\s*/, '');
      setClonePrjtError(errorMessage);
      setTimeout(() => {
        setClonePrjtError('');
        setCloningInProgress(false);
      }, 3000);
    }
  }, [handleOperationSuccess, formInput, account, selectedProject, cloneProject]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button className="space-y-[1px]" type="button" onClick={() => toggleDropdown(project.projectId)}>
        <div className="h-[4px] w-[4px] cursor-pointer rounded-full bg-black"></div>
        <div className="h-[4px] w-[4px] cursor-pointer rounded-full bg-black"></div>
        <div className="h-[4px] w-[4px] cursor-pointer rounded-full bg-black"></div>
      </button>
      {openDropdown === project.projectId && (
        <div className="dropdown-container absolute right-0 top-full z-10 w-[110px] rounded border border-gray-300 bg-white shadow-lg">
          <ul>
            <li className="hover:bg-gray-200">
              <button className="block cursor-pointer px-[27px] py-[9px] text-sm text-gray-700" onClick={() => navigateToForm('view')}>
                View
              </button>
            </li>
            <li className="hover:bg-gray-200">
              <button className="block cursor-pointer px-[27px] py-[9px] text-sm text-gray-700" onClick={() => navigateToForm('edit')}>
                Edit
              </button>
            </li>
            <li className="hover:bg-gray-200">
              <button className="block cursor-pointer px-[27px] py-[9px] text-sm text-gray-700" onClick={() => handleOpenClone(project)}>
                Clone
              </button>
            </li>
            <li className="hover:bg-gray-200">
              <button
                className="block cursor-pointer px-[27px] py-[9px] text-sm text-gray-700"
                onClick={() => openPopupDialog(project.shoppingListId, project.projectId, project.version, project.projectName, 'delete')}
              >
                Delete
              </button>
            </li>
            {(activeTab === 'open' || activeTab === 'my') && (
              <li className="hover:bg-gray-200">
                <button
                  className="block cursor-pointer px-[27px] py-[9px] text-sm text-gray-700"
                  onClick={() => openPopupDialog(project.shoppingListId, project.projectId, project.version, project.projectName, 'closed')}
                >
                  Close
                </button>
              </li>
            )}
            {(activeTab === 'closed' || activeTab === 'completed') && (
              <li className="hover:bg-gray-200">
                <button
                  className="block cursor-pointer px-[27px] py-[9px] text-sm text-gray-700"
                  onClick={() => openPopupDialog(project.shoppingListId, project.projectId, project.version, project.projectName, 'open')}
                >
                  Open
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
      <div className="popup-container">
        <ConfirmationPopup
          isOpen={openPopup}
          selectedProject={selectedProject}
          onClose={handlePopupClose}
          dropDownState={dropDownState}
          deleteProject={deleteProject}
          updateProjectStatus={updateProjectStatus}
          handleOperationSuccess={handleOperationSuccess}
          activeTab={activeTab}

        />
      </div>
      <div className="popup-container">
        <CloneProjectPopup
          isOpen={isClonePopupOpen}
          onClose={handleCloseClone}
          formInput={formInput}
          formError={formError}
          onConfirm={handleConfirmClone}
          selectedProject={selectedProject}
          setFormInput={setFormInput}
          clonePrjtSuccess={clonePrjtSuccess}
          clonePrjtError={clonePrjtError}
          cloningInProgress={cloningInProgress}
        />
      </div>
    </div>
  );
};
export const DropDown = React.memo(DropDownCoponent);

interface ConfirmationPopupProps {
  isOpen: boolean;
  selectedProject: { id: string; projectId: string; version: number; name: string } | null;
  onClose: (response: 'success' | 'canceled') => void;
  dropDownState: string;
  deleteProject: (payload: DeleteProjectPayload) => void;
  updateProjectStatus: (payload: UpdateProjectStatusPayload) => void;
  activeTab: any;
  handleOperationSuccess: () => void;
}

const ConfirmationPopupComponent = ({ isOpen, selectedProject, onClose, dropDownState, deleteProject, updateProjectStatus, handleOperationSuccess, activeTab }: ConfirmationPopupProps) => {
  const [localShowSuccess, setLocalShowSuccess] = useState<string>('');

  const handleDelete = useCallback(async () => {
    try {
      let label = '';
      if (dropDownState == 'delete') {
        if (selectedProject) {
          const payload = { id: selectedProject.id, version: selectedProject.version };
          label = 'deleted';
          await deleteProject(payload);
        }
      } else {
        if (selectedProject) {
          const payload = {
            shoppingListId: selectedProject.id,
            version: selectedProject.version,
            status: dropDownState,
            fieldName: 'status',
          };
          label = 'closed';
          if (activeTab == 'closed' || activeTab == 'completed') {
            label = 'opened';
          }
          await updateProjectStatus(payload);
        }
      }
      setLocalShowSuccess(`Project ${label} successfully.`);
      handleOperationSuccess();
      setTimeout(() => {
        onClose('success');
      }, 3000);
    } catch (err) {
      onClose('canceled');
    }
  }, [dropDownState, selectedProject, activeTab, deleteProject, updateProjectStatus, onClose, handleOperationSuccess]);

  const handleCancel = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    onClose('canceled');
  }, [onClose]);

  let lable = 'delete';
  if (dropDownState == 'open') {
    lable = 'open';
  } else if (dropDownState == 'closed') {
    lable = 'close';
  }

  if (!isOpen || !selectedProject) return null;

  return (
    <Popup isOpen={isOpen} bodyClassNames="sm:mx-unset max-w-[380px]">
      <div className="break-words p-[25px]">
        <p className="mt-[27px] whitespace-normal text-gray-700">
          Are you sure you want to {lable} <br /> <strong>{selectedProject.name}</strong>
        </p>
        <div className="mt-12 flex gap-[36px] space-x-[4px]">
          <Button variant={'secondary'} className="!py-[9px]text-sm cursor-pointer !border-[#dee2e6] !px-[27px] font-medium" type="button" onClick={handleDelete}>
            Yes, please {lable}
          </Button>
          <Button variant={'secondary'} className="!py-[9px]text-sm cursor-pointer !border-[#dee2e6] !px-[27px] font-medium" type="button" onClick={handleCancel}>
            Cancel
          </Button>
        </div>
        <div className="mt-[15px] text-green-700">{localShowSuccess}</div>
      </div>
    </Popup>
  );
};
export const ConfirmationPopup = React.memo(ConfirmationPopupComponent);

const ProjectHeadingSectionComponent = () => {
  const { locale } = useParams();
  const handleButtonClick = useCallback(() => {
    window.location.href = `/${locale}/project-builder/create`;
  }, [locale]);
  return (
    <div className="my-5 lg:mb-6 flex w-full flex-col text-3xl md:flex-row md:justify-between">
      <span className="mb-8 lg:mb-0">My Projects</span>
      <div className="flex items-start gap-2.5">
        <Button className='cursor-pointer !bg-[#1A1A1A] !text-[16px] !py-[12px] !px-[18px]' onClick={handleButtonClick}> Add New Project</Button>
      </div>
    </div>
  );
};
export const ProjectHeadingSection = React.memo(ProjectHeadingSectionComponent);

const TableViewComponent = ({
  projects,
  sortState,
  handleSortForDesktop,
  ProjectDetail,
  setOpenDropdown,
  openDropdown,
  handleOperationSuccess,
  activeTab,
  deleteProject,
  updateProjectStatus,
}: {
  projects: ProjectList;
  sortState: { column: number | null; direction: 'up' | 'down' | null };
  handleSortForDesktop: (index: number, direction: 'up' | 'down', sortingValue?: string) => void;
  ProjectDetail: React.FC<{ label?: string; value?: string }>;
  setOpenDropdown: React.Dispatch<React.SetStateAction<any>>;
  openDropdown: any;
  handleOperationSuccess: () => void;
  activeTab: string;
  deleteProject: (payload: DeleteProjectPayload) => void;
  updateProjectStatus: (payload: UpdateProjectStatusPayload) => void;
}) => (
  <div>
    <Table.Container className="w-full max-w-full table-fixed">
      <Table.Head>
        <Table.Row>
          {tableHeader.map((e, index) => (
            <Table.Cell key={e.label}>
              <div className="flex items-center gap-2">
                <Typography className="text-12">{e.label}</Typography>
                {e.sort && (
                  <div>
                    <span className={`cursor-pointer ${sortState.column === index && sortState.direction === 'up' ? 'text-black' : 'text-[#767676]'}`} onClick={() => handleSortForDesktop(index, 'up', e.valueAsc)}>
                      ↑
                    </span>
                    <span className={`cursor-pointer ${sortState.column === index && sortState.direction === 'down' ? 'text-black' : 'text-[#767676]'}`} onClick={() => handleSortForDesktop(index, 'down', e.valueDesc)}>
                      ↓
                    </span>
                  </div>
                )}
              </div>
            </Table.Cell>
          ))}
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {projects?.results?.map((project, index) => (
          <Table.Row key={project.projectId} className={`${index % 2 === 1 ? 'bg-gray-200' : ''} mb-4.5 w-full`}>
            <Table.Cell className="break-words">
              <Link link={`view?projectId=${project?.shoppingListId}`}>
                <Typography className="decoration-3 whitespace-normal text-16 !font-medium leading-6 text-[#090909] underline">{project?.projectName}</Typography>
              </Link>
            </Table.Cell>
            <Table.Cell className="w-[15%]">
              <ProjectDetail value={projectObj[project?.projectType ?? ''] || 'NA'} />
            </Table.Cell>
            <Table.Cell className="w-[13%]">
              <ProjectDetail value={project?.projectLocation || 'NA'} />
            </Table.Cell>
            <Table.Cell className="w-[12%]">
              <ProjectDetail value={project.projectCreatedDate ? formatDate(project.projectCreatedDate) : 'N/A'} />
            </Table.Cell>
            <Table.Cell className="relative w-[150px]">
              <div className="flex justify-between">
                <ProjectDetail value={formatDate(project.lastModifiedAt)} />
                <DropDown
                  project={project}
                  setOpenDropdown={setOpenDropdown}
                  openDropdown={openDropdown}
                  handleOperationSuccess={handleOperationSuccess}
                  activeTab={activeTab}
                  deleteProject={deleteProject}
                  updateProjectStatus={updateProjectStatus}
                />
              </div>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Container>
  </div>

);
export const TableView = React.memo(TableViewComponent);

const MobileViewComponent = ({
  openSort,
  setOpenSort,
  options,
  handleSort,
  handleClose,
  projects,
  setOpenDropdown,
  openDropdown,
  handleOperationSuccess,
  activeTab,
  deleteProject,
  updateProjectStatus,
}: {
  openSort: boolean;
  setOpenSort: React.Dispatch<React.SetStateAction<boolean>>;
  options: { name: string; checked: boolean; value: string }[];
  handleSort: (index: number) => void;
  handleClose: () => void;
  projects: ProjectList;
  setOpenDropdown: React.Dispatch<React.SetStateAction<any>>;
  openDropdown: any;
  handleOperationSuccess: () => void;
  activeTab: string;
  deleteProject: (payload: DeleteProjectPayload) => void;
  updateProjectStatus: (payload: UpdateProjectStatusPayload) => void;
}) => (
  <>
    <div className="mb-[24px] mt-[48px] md:mt-12 flex w-full flex-col text-3xl lg:flex-row lg:justify-between">
      <Button variant={'secondary'} className=" w-full lg:w-auto" type="button" onClick={() => setOpenSort(!openSort)}>
        ↑↓ Sort
      </Button>
    </div>
    <Popup isOpen={openSort} bodyClassNames="mx-5 sm:mx-unset  max-w-[535px]">
      <div className="mb-5 flex items-center pl-6 pt-8">
        <Typography className="flex flex-1 justify-start !text-20 !text-[090909]">Sortby following field</Typography>
        <div className="cursor-p pr-4" onClick={handleClose}>
          <Icon />
        </div>
      </div>
      <div className="pt-5">
        {options?.map((option, index) => (
          <div key={index} className={`${index !== 0 ? 'mt-3' : ''} ${index === options?.length - 1 ? 'mb-5' : ''} flex cursor-pointer items-center gap-4 pl-6`} onClick={() => handleSort(index)}>
            <Radio name={option.name} checked={option.checked} />
            <div>
              <p className="text-14 font-medium">{option.name}</p>
            </div>
          </div>
        ))}
      </div>
    </Popup>

    <ProjectListRecords projects={projects} setOpenDropdown={setOpenDropdown} openDropdown={openDropdown} handleOperationSuccess={handleOperationSuccess} activeTab={activeTab} deleteProject={deleteProject} updateProjectStatus={updateProjectStatus} />

  </>
);
export const MobileView = React.memo(MobileViewComponent);

export default ProjectList;


